// Router im Bibliotheks-Modus von react-router 7 (createBrowserRouter). Die Routen der Module stammen aus der
// erzeugten Registry (web/registry.gen.ts); jede Seite ist ein eigener, nachgeladener Teil.
//
//   /login                      Anmeldung
//   Routen mit layout 'public'  vor der Anmeldung, ohne Rahmen (Einladung annehmen)
//   /  …                        App-Rahmen (AppShell): Start, Routen der Module mit layout 'app', 404
//   /documents/:id              Beleg irgendeines Moduls: Belegart nachschlagen → Seite des Moduls (DocumentRoute)
//   Routen mit layout 'fullscreen'   ohne Rahmen (Kasse, Scan-Abläufe), aber hinter der Sitzungsprüfung
//   /kit  …                     Schaukasten (nur in der Entwicklung)
import { lazy, Suspense, type ComponentType } from 'react'
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router'
import { kitRoutes as apiKitRoutes } from './api/kit/routes'
import { Forbidden, NotFound, PageSkeleton, RouteErrorBoundary } from './api/pages/states'
import { hasFeature, hasPermission } from './api/permissions'
import { useSession } from './api/session'
import type { WebModule, WebRoute } from './module'
import { webModules } from './registry.gen'
import { AppShell } from './shell/AppShell'
import { DocumentRoute } from './shell/DocumentRoute'
import { AppRoot, CrashPage, SessionGate, Splash } from './shell/runtime'
import { StartPage } from './shell/StartPage'
import { kitRoutes as uiKitRoutes } from './ui/kit/routes'

export interface RouteHandle {
  module: string
  /** deutsch – Titel im Browser-Tab und in den Brotkrumen */
  title: string
  permission?: string
  feature?: string | readonly string[]
  layout: 'app' | 'fullscreen' | 'public'
}

/**
 * Seite einer Modul-Route: prüft Recht und Funktionsschalter (Anzeige – maßgeblich bleibt der Server) und zeigt
 * ein Skelett mit dem Titel, bis der Code der Seite geladen ist. Der Seitenwechsel selbst ist sofort da.
 */
function modulePage(route: WebRoute): ComponentType {
  const Page = lazy(route.lazy)
  function ModulePage() {
    const session = useSession()
    // Seiten vor der Anmeldung (Einladung annehmen) kennen keine Sitzung – und damit weder Recht noch Schalter
    if (route.layout === 'public') {
      return (
        <Suspense fallback={<Splash />}>
          <Page />
        </Suspense>
      )
    }
    if (!hasFeature(session, route.feature)) return <Forbidden feature />
    if (!hasPermission(session, route.permission, 'any')) return <Forbidden permission={route.permission} />
    return (
      <Suspense fallback={<PageSkeleton title={route.title} />}>
        <Page />
      </Suspense>
    )
  }
  return ModulePage
}

export function moduleRoutes(modules: readonly WebModule[]): RouteObject[] {
  return modules.flatMap((module) =>
    module.routes.map((route): RouteObject => {
      const Component = modulePage(route)
      const handle: RouteHandle = {
        module: module.id,
        title: route.title,
        permission: route.permission,
        feature: route.feature,
        layout: route.layout ?? 'app',
      }
      return {
        path: route.path,
        handle,
        // löst sofort auf: Das Nachladen der Seite übernimmt <Suspense> in ModulePage
        lazy: async () => ({ Component }),
        ErrorBoundary: RouteErrorBoundary,
      }
    }),
  )
}

/**
 * Rahmen der Vollbild-Seiten (Kasse, Scan-Abläufe): hält links und rechts die sicheren Bereiche frei (Kamera-Aussparung,
 * quer liegende Navigationsleiste in der App – docs/mobile/SPEZIFIKATION.md §4.7). Oben und unten sorgen die Seiten
 * selbst dafür (Kopfzeile mit pt-safe, Aktionsleiste mit pb-safe). Im Browser sind die Werte 0.
 */
function FullscreenFrame() {
  return (
    <div className="pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]" data-ui="fullscreen">
      <Outlet />
    </div>
  )
}

const layoutOf = (route: RouteObject): string => (route.handle as RouteHandle | undefined)?.layout ?? 'app'

// Schaukästen: nur in der Entwicklung, außerhalb des App-Rahmens. Jedes Paket sammelt seine Routen selbst
// (web/ui/kit/routes.ts, web/api/kit/routes.ts) – hier werden sie nur angehängt.
const kitRoutes: RouteObject[] = import.meta.env.DEV ? [...uiKitRoutes, ...apiKitRoutes] : []

export function buildRoutes(modules: readonly WebModule[]): RouteObject[] {
  const routes = moduleRoutes(modules)
  const Root = (): React.JSX.Element => <AppRoot modules={modules} />
  return [
    {
      // solange der Code der ersten Seite nachlädt: derselbe ruhige Startbildschirm wie bei der Sitzungsprüfung
      HydrateFallback: Splash,
      children: [
        {
          Component: Root,
          ErrorBoundary: CrashPage,
          children: [
            { path: '/login', lazy: async () => ({ Component: (await import('./shell/LoginPage')).LoginPage }) },
            // Seiten der Module vor der Anmeldung (layout 'public': Einladung annehmen)
            ...routes.filter((route) => layoutOf(route) === 'public'),
            {
              Component: SessionGate,
              children: [
                {
                  Component: AppShell,
                  children: [
                    {
                      index: true,
                      Component: StartPage,
                      handle: { title: 'Start' },
                      ErrorBoundary: RouteErrorBoundary,
                    },
                    // Belege aller Module: Belegart nachschlagen, zur Seite ihres Moduls leiten
                    {
                      path: '/documents/:id',
                      Component: DocumentRoute,
                      handle: { title: 'Beleg' },
                      ErrorBoundary: RouteErrorBoundary,
                    },
                    ...routes.filter((route) => layoutOf(route) === 'app'),
                    { path: '*', Component: NotFound, handle: { title: 'Nicht gefunden' } },
                  ],
                },
                { Component: FullscreenFrame, children: routes.filter((route) => layoutOf(route) === 'fullscreen') },
              ],
            },
          ],
        },
        ...kitRoutes,
      ],
    },
  ]
}

export const router = createBrowserRouter(buildRoutes(webModules))
